import React, { useContext, useRef } from 'react';
import PropTypes from 'prop-types';

import history from 'utils/history';
import { LayoutContext } from 'components/Layout/layout';
import { Breadcrumbs, Spinner, Wrapper, Button as ComponentsButton } from 'components';
import { useTranslation } from 'react-i18next';
import * as Ui from './Page.styles';

const Page = ({
  children,
  HeaderChildren,
  logo,
  title,
  breadcrumbs,
  description,
  withBackButton = true,
  loading = false,
  onBack,
  Button,
  AbsoluteButton,
  contentRef,
  footer,
  skipPadding = false,
  noPaddingBottom = false,
  ...rest
}) => {
  const { sideBarCollapsed } = useContext(LayoutContext) || {};
  const footerRef = useRef();
  const { t } = useTranslation('pages');

  const renderFooter = () => {
    if (footer && footer?.visible) {
      return (
        <Ui.Footer ref={footerRef} sideBarCollapsed={sideBarCollapsed}>
          {footer.component}
        </Ui.Footer>
      );
    }
    return null;
  };

  return (
    <Ui.Container {...rest}>
      {(logo || title || description || breadcrumbs) && (
        <Ui.TitleWrapper $titleSeparator={rest.titleSeparator} $noPaddingBottom={noPaddingBottom}>
          <Wrapper d-flex style={{ width: '100%' }}>
            {logo && <Ui.Icon>{logo}</Ui.Icon>}
            <Wrapper d-flex flex-column pt-2 pl-2 style={{ width: '100%', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
                {title && <Ui.HeaderTitle>{title}</Ui.HeaderTitle>}
                {description && <Ui.HeaderDescription>{description}</Ui.HeaderDescription>}
              </div>
              {!!HeaderChildren && HeaderChildren}
            </Wrapper>
          </Wrapper>

          {!!Button && (
            <Ui.ButtonContainer>
              <Button />
            </Ui.ButtonContainer>
          )}

          {!!AbsoluteButton && (
            <Ui.AbsoluteButtonWrapper>
              <AbsoluteButton />
            </Ui.AbsoluteButtonWrapper>
          )}
        </Ui.TitleWrapper>
      )}
      {withBackButton && (
        <ComponentsButton
          className="mb-4 ml-3"
          variant="secondary"
          onClick={() => {
            if (onBack) {
              onBack();
            } else {
              history.goBack();
            }
          }}
        >
          {t('pageNotFound.goBack')}
        </ComponentsButton>
      )}
      {loading ? (
        <div
          style={{ height: `calc(100vh - 360px)` }}
          className="d-flex justify-content-center align-items-center w-100"
        >
          <Spinner />
        </div>
      ) : (
        <Ui.MainContent ref={contentRef} $skipPadding={skipPadding} $hasFooter={footer?.visible}>
          {children}
          {renderFooter()}
        </Ui.MainContent>
      )}
    </Ui.Container>
  );
};

Page.propTypes = {
  logo: PropTypes.node,
  title: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.node.isRequired,
      to: PropTypes.string,
    }),
  ),
  description: PropTypes.node,
  children: PropTypes.any,
  HeaderChildren: PropTypes.any,
  withBackButton: PropTypes.bool,
  loading: PropTypes.bool,
  Button: PropTypes.any,
  AbsoluteButton: PropTypes.any,
  contentRef: PropTypes.shape({ current: PropTypes.object }),
  onBack: PropTypes.func,
  avedaPage: PropTypes.bool,
  footer: PropTypes.object,
  skipPadding: PropTypes.bool,
  noPaddingBottom: PropTypes.bool,
};

export default Page;
